import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { api } from '@/api';
import { toast } from 'sonner';
import usePermissions from '@/hooks/usePermissions';
import { 
  Plus, 
  Search, 
  Filter, 
  FileQuestion, 
  Clock,
  AlertTriangle,
  CheckCircle2,
  ArrowRight,
  RefreshCw
} from 'lucide-react';
import PrintScheduleButton from '@/components/PrintScheduleButton';

const STATUS_COLORS = {
  Draft: 'bg-slate-100 text-slate-700 border-slate-200',
  Issued: 'bg-blue-100 text-blue-700 border-blue-200',
  'Awaiting Response': 'bg-amber-100 text-amber-700 border-amber-200',
  Answered: 'bg-blue-100 text-blue-700 border-blue-200',
  Closed: 'bg-green-100 text-green-700 border-green-200',
  Overdue: 'bg-red-100 text-red-700 border-red-200'
};

const PRIORITY_COLORS = {
  Low: 'bg-slate-50 text-slate-600',
  Medium: 'bg-blue-50 text-blue-600',
  High: 'bg-orange-50 text-orange-600',
  Critical: 'bg-red-50 text-red-700'
};

const AGING_COLORS = {
  Green: 'text-green-600',
  Amber: 'text-amber-600',
  Red: 'text-red-600',
  Overdue: 'text-red-700 font-bold',
  Answered: 'text-blue-600'
};

export default function RFIList({ projectId, onCreateClick, onViewRFI, initialFilter = '', project }) {
  const [rfis, setRfis] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Map initialFilter to status
  const getInitialStatus = () => {
    if (initialFilter === 'open') return 'all'; // Show all non-closed
    if (initialFilter === 'awaiting_action' || initialFilter === 'awaiting') return 'Awaiting Response';
    if (initialFilter === 'overdue') return 'Overdue';
    return 'all';
  };
  
  const [filterStatus, setFilterStatus] = useState(getInitialStatus());
  const [filterDiscipline, setFilterDiscipline] = useState('all');
  const permissions = usePermissions();
  const [filterPriority, setFilterPriority] = useState(initialFilter === 'critical' ? 'Critical' : 'all');
  const [disciplines, setDisciplines] = useState([]);
  const [filterOverdue, setFilterOverdue] = useState(initialFilter === 'overdue');
  const [filterOpen, setFilterOpen] = useState(initialFilter === 'open');

  useEffect(() => {
    loadRFIs();
    loadDisciplines();
  }, [projectId]);

  const loadRFIs = async () => {
    try {
      const params = {};
      if (filterStatus && filterStatus !== 'all') params.status = filterStatus;
      if (filterDiscipline && filterDiscipline !== 'all') params.discipline = filterDiscipline;
      if (filterPriority && filterPriority !== 'all') params.priority = filterPriority;
      
      const response = await api.rfi.getByProject(projectId, params);
      setRfis(response.data.rfis);
      setStats(response.data.stats);
    } catch (error) {
      console.error('RFI load error:', error.response?.data || error.message);
      const errorMsg = error.response?.data?.detail || 'Failed to load RFIs';
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  const loadDisciplines = async () => {
    try {
      const response = await api.rfi.getDisciplines();
      setDisciplines(response.data);
    } catch (error) {
      console.error('Failed to load disciplines');
    }
  };

  useEffect(() => {
    if (!loading) {
      loadRFIs();
    }
  }, [filterStatus, filterDiscipline, filterPriority]);

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const filteredRFIs = rfis.filter(rfi => {
    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      const matches = (
        rfi.rfi_number?.toLowerCase().includes(query) ||
        rfi.title?.toLowerCase().includes(query) ||
        rfi.discipline?.toLowerCase().includes(query)
      );
      if (!matches) return false;
    }
    
    // Open filter - exclude closed
    if (filterOpen && rfi.status === 'Closed') {
      return false;
    }
    
    // Overdue filter
    if (filterOverdue) {
      if (rfi.aging_category !== 'Overdue') return false;
    }
    
    return true;
  });
  
  // Get active filter name for display
  const getActiveFilterName = () => {
    if (filterOpen) return 'Open Items';
    if (filterOverdue) return 'Overdue Items';
    if (filterStatus === 'Awaiting Response') return 'Awaiting Response';
    if (filterPriority === 'Critical') return 'Critical Priority';
    return null;
  };
  
  const activeFilterName = getActiveFilterName();
  
  // Clear all filters
  const clearFilters = () => {
    setFilterOpen(false);
    setFilterOverdue(false);
    setFilterStatus('all');
    setFilterPriority('all');
    setFilterDiscipline('all');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="h-6 w-6 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="rfi-list">
      {/* Active Filter Banner */}
      {activeFilterName && (
        <div className="flex items-center justify-between p-3 bg-purple-50 border border-purple-200 rounded-lg">
          <div className="flex items-center gap-2">
            <Filter className="h-4 w-4 text-purple-600" />
            <span className="text-sm font-medium text-purple-800">
              Filtered by: <span className="font-bold">{activeFilterName}</span>
            </span>
            <span className="bg-purple-100 text-purple-700 text-xs px-2 py-1 rounded">{filteredRFIs.length} items</span>
          </div>
          <button onClick={clearFilters} className="text-sm text-purple-600 hover:text-purple-800 font-medium">
            Clear Filter
          </button>
        </div>
      )}
      
      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <div className="bg-white border border-slate-200 rounded-sm p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Total</p>
          <p className="text-2xl font-barlow font-bold text-slate-900">{stats.total || 0}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Issued</p>
          <p className="text-2xl font-barlow font-bold text-blue-900">{stats.issued || 0}</p>
        </div>
        <div className="bg-amber-50 border border-amber-200 rounded-sm p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-amber-600">Awaiting</p>
          <p className="text-2xl font-barlow font-bold text-amber-900">{stats.awaiting_response || 0}</p>
        </div>
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Answered</p>
          <p className="text-2xl font-barlow font-bold text-blue-900">{stats.answered || 0}</p>
        </div>
        <div className="bg-red-50 border border-red-200 rounded-sm p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-red-600">Overdue</p>
          <p className="text-2xl font-barlow font-bold text-red-900">{stats.overdue || 0}</p>
        </div>
        <div className="bg-purple-50 border border-purple-200 rounded-sm p-4">
          <p className="text-xs font-bold uppercase tracking-wider text-purple-600">Critical</p>
          <p className="text-2xl font-barlow font-bold text-purple-900">{stats.critical || 0}</p>
        </div>
      </div>

      {/* Response Time Stats */}
      {stats.avg_response_time !== null && stats.avg_response_time !== undefined && (
        <div className="bg-blue-50 border border-blue-200 rounded-sm p-4">
          <div className="flex items-center gap-2 mb-2">
            <Clock className="h-4 w-4 text-blue-600" />
            <p className="text-xs font-bold uppercase tracking-wider text-blue-600">Response Time Performance</p>
          </div>
          <div className="flex flex-wrap gap-6 text-sm">
            <div>
              <span className="text-slate-600">Average: </span>
              <span className="font-bold text-blue-700">{stats.avg_response_time} days</span>
            </div>
            {stats.fastest_response !== null && (
              <div>
                <span className="text-slate-600">Fastest: </span>
                <span className="font-medium text-emerald-600">{stats.fastest_response} days</span>
              </div>
            )}
            {stats.slowest_response !== null && (
              <div>
                <span className="text-slate-600">Slowest: </span>
                <span className="font-medium text-red-600">{stats.slowest_response} days</span>
              </div>
            )}
            <div>
              <span className="text-slate-600">Total Answered: </span>
              <span className="font-medium text-slate-700">{stats.total_answered}</span>
            </div>
          </div>
        </div>
      )}

      {/* Aging Breakdown */}
      {(stats.aging_green > 0 || stats.aging_amber > 0 || stats.aging_red > 0) && (
        <div className="flex gap-4">
          <div className="flex items-center gap-2 px-3 py-1 bg-emerald-50 border border-emerald-200 rounded-sm">
            <div className="w-3 h-3 rounded-full bg-emerald-500"></div>
            <span className="text-xs font-medium text-emerald-700">{stats.aging_green || 0} Green (≤7d)</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-amber-50 border border-amber-200 rounded-sm">
            <div className="w-3 h-3 rounded-full bg-amber-500"></div>
            <span className="text-xs font-medium text-amber-700">{stats.aging_amber || 0} Amber (8-14d)</span>
          </div>
          <div className="flex items-center gap-2 px-3 py-1 bg-red-50 border border-red-200 rounded-sm">
            <div className="w-3 h-3 rounded-full bg-red-500"></div>
            <span className="text-xs font-medium text-red-700">{stats.aging_red || 0} Red (&gt;14d)</span>
          </div>
        </div>
      )}

      {/* Header & Filters */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <FileQuestion className="h-5 w-5 text-slate-700" />
          <h3 className="text-lg font-barlow font-bold uppercase text-slate-900">
            RFI Register
          </h3>
          <PrintScheduleButton moduleType="RFI" data={rfis} project={project} />
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search RFIs..."
              className="pl-10 w-[200px]"
              data-testid="search-rfi"
            />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="Draft">Draft</SelectItem>
              <SelectItem value="Issued">Issued</SelectItem>
              <SelectItem value="Awaiting Response">Awaiting</SelectItem>
              <SelectItem value="Answered">Answered</SelectItem>
              <SelectItem value="Closed">Closed</SelectItem>
              <SelectItem value="Overdue">Overdue</SelectItem>
            </SelectContent>
          </Select>
          <Select value={filterDiscipline} onValueChange={setFilterDiscipline}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="Discipline" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Disciplines</SelectItem>
              {disciplines.map(d => (
                <SelectItem key={d} value={d}>{d}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          {permissions.canCreateRFI() && (
            <Button
              onClick={onCreateClick}
              className="bg-slate-900 text-white hover:bg-slate-800"
              data-testid="create-rfi-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create RFI
            </Button>
          )}
          <Button
            variant="outline"
            onClick={loadRFIs}
            className="border-slate-300"
            data-testid="refresh-rfi-button"
          >
            <RefreshCw className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* RFI Table */}
      <div className="bg-white border border-slate-200 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-200">
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">RFI #</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Title</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Discipline</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Priority</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Issued</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Response Due</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Days Out</th>
                <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filteredRFIs.length === 0 ? (
                <tr>
                  <td colSpan={9} className="px-4 py-8 text-center">
                    <FileQuestion className="h-12 w-12 text-slate-300 mx-auto mb-3" />
                    <p className="text-slate-500">No RFIs found</p>
                    <Button
                      onClick={onCreateClick}
                      variant="outline"
                      className="mt-4"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Create First RFI
                    </Button>
                  </td>
                </tr>
              ) : (
                filteredRFIs.map((rfi) => (
                  <tr 
                    key={rfi.id} 
                    className="hover:bg-slate-50/50 cursor-pointer"
                    onClick={() => onViewRFI(rfi.id)}
                    data-testid={`rfi-row-${rfi.rfi_number || 'draft'}`}
                  >
                    <td className="px-4 py-3">
                      <span className="font-mono font-medium text-slate-900">
                        {rfi.rfi_number || <span className="text-amber-600 italic">Draft</span>}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="max-w-[250px]">
                        <p className="font-medium text-slate-900 truncate">{rfi.title}</p>
                        {rfi.affects_critical_path && (
                          <Badge className="bg-red-100 text-red-700 text-xs mt-1">Critical Path</Badge>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">{rfi.discipline}</span>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={PRIORITY_COLORS[rfi.priority] || ''}>
                        {rfi.priority}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <Badge className={STATUS_COLORS[rfi.status] || ''}>
                        {rfi.status}
                      </Badge>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">{formatDate(rfi.date_issued)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className="text-sm text-slate-600">{formatDate(rfi.required_response_date)}</span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-medium ${AGING_COLORS[rfi.aging_category] || ''}`}>
                        {rfi.days_outstanding !== null && rfi.days_outstanding !== undefined 
                          ? `${rfi.days_outstanding} days` 
                          : '-'}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onViewRFI(rfi.id);
                        }}
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
